// Maintenance — platform self-upkeep view. Shows the latest run of each maintenance op
// (docker-prune, backup+retention, log-rotate, health-report, db-maintenance, restart),
// its key metric, and how it was triggered (scheduled host timer / out-of-band / dispatch),
// with a "Run now" button. Schedules are owned by host systemd timers; the latest
// health-report surfaces disk usage + the timers' next-run.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Wrench, RefreshCw, Play, HardDrive, CheckCircle, XCircle, Clock } from 'lucide-react';
import { apiRequest } from '@/lib/supabase';
import { EmptyState } from '@/components/shared/EmptyState';
import { useToast } from '@/components/shared/ToastProvider';
import { TypedConfirmDialog } from '@/components/shared/TypedConfirmDialog';

interface Job {
  id: string; instance_id: string; op_key: string;
  status: 'pending' | 'claimed' | 'done' | 'failed';
  result: Record<string, unknown>; source?: string;
  enqueued_at: string; reported_at: string | null;
}

const MAINT_OPS: { key: string; label: string; blurb: string }[] = [
  { key: 'health-report', label: 'Health report', blurb: 'Disk, inodes, container & image counts' },
  { key: 'backup', label: 'Backup', blurb: 'DB dump + retention' },
  { key: 'docker-prune', label: 'Docker prune', blurb: 'Reclaim images/cache/containers (never volumes)' },
  { key: 'log-rotate', label: 'Log rotation', blurb: 'Enforce container log limits' },
  { key: 'db-maintenance', label: 'DB maintenance', blurb: 'VACUUM (ANALYZE)' },
  { key: 'restart', label: 'Restart', blurb: 'Health-gated rolling restart' },
];

const card: React.CSSProperties = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: 'var(--shadow-card)' };
const btnSec: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, background: 'var(--surface)', color: 'var(--text-primary)', border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };

function rel(iso: string | null): string {
  if (!iso) return 'never';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function inner(j?: Job): Record<string, unknown> {
  if (!j) return {};
  const r = j.result || {};
  return (r.result && typeof r.result === 'object' ? r.result : r) as Record<string, unknown>;
}

// One-line metric per op from the reported result.
function metric(op: string, j?: Job): string {
  if (!j) return '—';
  const d = inner(j);
  const n = (v: unknown) => (typeof v === 'number' ? v : undefined);
  switch (op) {
    case 'health-report': {
      const disk = n(d.disk_use_pct), c = n(d.containers_running), img = n(d.images);
      return disk !== undefined ? `disk ${disk}% · ${c ?? '?'} running · ${img ?? '?'} images` : '—';
    }
    case 'backup': {
      const mb = n(d.size_mb), kept = n(d.kept);
      return mb !== undefined ? `${mb} MB${kept !== undefined ? ` · keep ${kept}` : ''}` : '—';
    }
    case 'docker-prune': {
      if (d.dry_run) return 'dry-run (nothing removed)';
      const rec = Array.isArray(d.reclaimed) ? (d.reclaimed as string[]) : [];
      return rec.length ? rec.join(' · ').replace(/Total reclaimed space:\s*/g, '') : 'pruned';
    }
    case 'log-rotate': {
      const w = n(d.containers_without_rotation);
      if (d.mode === 'enforce') return d.changed ? 'updated daemon.json (restart pending)' : 'already enforced';
      return w !== undefined ? `${w} container(s) unrotated` : '—';
    }
    case 'db-maintenance': {
      const mb = n(d.db_size_mb_after);
      return mb !== undefined ? `db ${mb} MB` : 'vacuumed';
    }
    default:
      return j.status;
  }
}

function StatusDot({ status }: { status: Job['status'] }) {
  const map = {
    done: { c: 'var(--ps-pill-green-tx)', I: CheckCircle }, failed: { c: 'var(--ps-pill-red-tx)', I: XCircle },
    pending: { c: 'var(--ps-pill-amber-tx)', I: Clock }, claimed: { c: 'var(--accent)', I: Clock },
  } as const;
  const s = map[status] || map.pending;
  const I = s.I;
  return <I size={14} color={s.c} />;
}

export function MaintenancePage() {
  const { toast } = useToast();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  // Run now enqueues for the WHOLE fleet (cohort 'all'): confirmed by typing the operation key (DF-38).
  const [pendingOp, setPendingOp] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    apiRequest<Job[]>('/operations/maintenance').then((j) => setJobs(j || [])).catch(() => setJobs([])).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  // Latest job per op_key (the endpoint already returns latest per instance+op).
  const byOp = useMemo(() => {
    const m = new Map<string, Job>();
    for (const j of jobs) if (!m.has(j.op_key)) m.set(j.op_key, j);
    return m;
  }, [jobs]);

  // The newest health-report carries the host timers' next-run lines.
  const timers = useMemo(() => {
    const hr = byOp.get('health-report');
    const t = inner(hr).timers;
    return Array.isArray(t) ? (t as string[]) : [];
  }, [byOp]);

  const runNow = async (op: string) => {
    setBusy(op);
    try {
      await apiRequest('/operations/enqueue', { method: 'POST', body: JSON.stringify({ op_key: op, cohort: 'all' }) });
      toast({ kind: 'success', title: `Dispatched '${op}'`, body: 'Runs on the next check-in; result lands here.' });
      setTimeout(load, 800);
    } catch (e: unknown) {
      toast({ kind: 'error', title: 'Dispatch failed', body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(null);
      setPendingOp(null);
    }
  };

  return (
    <div style={{ padding: '24px 32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Wrench size={22} />
          <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-.01em', margin: 0 }}>Maintenance</h1>
        </div>
        <button onClick={load} style={btnSec}><RefreshCw size={13} /> Refresh</button>
      </div>

      {loading && jobs.length === 0 ? (
        <div style={{ ...card, padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14, marginBottom: 16 }}>
            {MAINT_OPS.map(({ key, label, blurb }) => {
              const j = byOp.get(key);
              return (
                <div key={key} style={{ ...card, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: 'var(--mono, monospace)', fontWeight: 700, fontSize: 13 }}>{key}</span>
                    {j && <StatusDot status={j.status} />}
                    {j?.source && j.source !== 'dispatch' && (
                      <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', background: 'var(--bg-muted)', padding: '1px 6px', borderRadius: 99 }}>{j.source}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{blurb}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{metric(key, j)}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                    <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>last run {rel(j?.reported_at || j?.enqueued_at || null)}</span>
                    <button onClick={() => setPendingOp(key)} disabled={busy === key}
                      style={{ ...btnSec, padding: '5px 10px', fontSize: 12, opacity: busy === key ? 0.6 : 1 }}>
                      <Play size={11} /> Run now
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ ...card, overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <HardDrive size={15} color="var(--text-secondary)" />
              <span style={{ fontSize: 14, fontWeight: 600 }}>Schedules (host systemd timers)</span>
            </div>
            {timers.length === 0 ? (
              <EmptyState icon="clock" title="No schedule reported yet"
                body="Schedules are owned by host systemd timers. The next health-report will surface their next-run times here." />
            ) : (
              <pre style={{ margin: 0, padding: '14px 20px', fontSize: 12, fontFamily: 'var(--mono, monospace)', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
                {timers.join('\n')}
              </pre>
            )}
          </div>
        </>
      )}

      <TypedConfirmDialog
        open={pendingOp !== null}
        title={pendingOp ? `Run '${pendingOp}' on the whole fleet?` : ''}
        body={pendingOp ? `This queues '${pendingOp}' for every install; each runs it on its next check-in. ${MAINT_OPS.find((o) => o.key === pendingOp)?.blurb ?? ''}`.trim() : ''}
        phrase={pendingOp ?? ''}
        confirmLabel="Run on all installs"
        busy={pendingOp !== null && busy === pendingOp}
        onConfirm={() => { if (pendingOp) void runNow(pendingOp); }}
        onCancel={() => setPendingOp(null)}
      />
    </div>
  );
}
